import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { languages, Language } from '../locales';

const FlagIcon: React.FC<{ language: Language }> = ({ language }) =>
  language.flagImg ? (
    <img
      src={language.flagImg}
      alt={language.name}
      className='w-5 h-auto rounded-sm object-contain'
    />
  ) : language.flag ? (
    <span className='text-base leading-none' aria-hidden='true'>
      {language.flag}
    </span>
  ) : null;

interface LanguageSwitcherProps {
  dropUp?: boolean;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ dropUp = false }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <div className='relative'>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className='bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 py-1 rounded-md text-sm font-medium transition-all duration-200 border border-white border-opacity-30 flex items-center gap-2'
      >
        <FlagIcon language={currentLanguage} />
        <span>{currentLanguage.code.toUpperCase()}</span>
        <ChevronDown
          className={`w-4 h-4 transform transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div
          className={`absolute right-0 bg-white rounded-md shadow-lg border border-gray-200 py-1 min-w-[120px] z-50 ${
            dropUp ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              className={`w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm ${
                i18n.language === language.code
                  ? 'bg-church-brown bg-opacity-10 text-church-brown'
                  : 'text-gray-700'
              }`}
            >
              <FlagIcon language={language} />
              <span>{language.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
